import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import './OverallSOP.css';

const OverallSOP = () => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState({});
  const [processingMessage, setProcessingMessage] = useState('');
  const [completionMessage, setCompletionMessage] = useState('');
  const [stepMessages, setStepMessages] = useState({});
  const navigate = useNavigate();

  // Check if step is completed based on last updated date
  const checkStepStatus = (step, lastUpdated) => {
    if (lastUpdated === 'N/A') return 'pending';
    
    const lastUpdatedDate = new Date(lastUpdated);
    const now = new Date();
    const diffInDays = (now - lastUpdatedDate) / (1000 * 60 * 60 * 24);
    
    switch (step) {
      case 1: // Amazon inventory - < 1 day
        return diffInDays < 1 ? 'completed' : 'pending';
      case 2: // Austin inventory - < 15 days
        return diffInDays < 15 ? 'completed' : 'pending';
      case 3: // Component inventory - < 30 days
        return diffInDays < 30 ? 'completed' : 'pending';
      default:
        return 'pending';
    }
  };

  // Initialize steps data
  useEffect(() => {
    const initializeSteps = async () => {
      setLoading(true);
      try {
        // Fetch last updated dates for each step
        const [amazonInventoryDate, austinInventoryDate, componentInventoryDate, financialForecastDate] = await Promise.all([
          fetchLastUpdatedDate('amazon_inventory'),
          fetchLastUpdatedDate('austin_inventory'),
          fetchLastUpdatedDate('component_inventory'),
          fetchLastUpdatedDate('financial_forecast')
        ]);

        const stepsData = [
          {
            id: 1,
            step: "Update Finished Goods inventory at Amazon",
            description: "Use the API to Pull in the latest inventory sitting at Amazon AWD and FBA, as we as in-transit to AWD",
            lastUpdated: amazonInventoryDate,
            status: checkStepStatus(1, amazonInventoryDate),
            action: 'fetch_now',
            actionText: 'Fetch Now',
            actionDisabled: false,
            actionMessage: '',
            completed: checkStepStatus(1, amazonInventoryDate) === 'completed'
          },
          {
            id: 2,
            step: "Update Finished Goods Inventory in Austin storage",
            description: "Manually input the amount of Finished Goods inventory sitting in the storage facility in Austin",
            lastUpdated: austinInventoryDate,
            status: checkStepStatus(2, austinInventoryDate),
            action: 'update_now',
            actionText: 'Update Now',
            actionDisabled: false,
            actionMessage: '',
            completed: checkStepStatus(2, austinInventoryDate) === 'completed',
            redirectTo: '/austin-inventory'
          },
          {
            id: 3,
            step: "Update Component Inventory at Sundeep",
            description: "Confirm that the latest inventory file with the component inventory at Sundeep has been imported",
            lastUpdated: componentInventoryDate,
            status: checkStepStatus(3, componentInventoryDate),
            action: 'update_now',
            actionText: 'Update Now',
            actionDisabled: false,
            actionMessage: '',
            completed: checkStepStatus(3, componentInventoryDate) === 'completed',
            redirectTo: '/component-inventory'
          },
          {
            id: 4,
            step: "Refresh Global Assumptions",
            description: "The global assumptions allow to overlay additional assumptions like additional marketing spend or performance that could drive overall sales volume above or below the Momentum Forecast",
            lastUpdated: 'N/A',
            status: 'pending',
            action: 'update_now',
            actionText: 'Update Now',
            actionDisabled: false,
            actionMessage: '',
            completed: false,
            redirectTo: '/global-forecasting'
          },
          {
            id: 5,
            step: "Confirm Historical Production",
            description: "Review prior production plans and schedule to clarify production that might be work-in-progress. Either it scheduled or not yet made or completed by not yet on the way to Amazon or Storage",
            lastUpdated: 'N/A',
            status: 'pending',
            action: 'update_now',
            actionText: 'Update Now',
            actionDisabled: false,
            actionMessage: '',
            completed: false,
            redirectTo: '/production-schedule'
          },
          {
            id: 6,
            step: "Refresh Production Plans",
            description: "Using the latest product sales forecast, current inventory, safety stock and production parameters update the production plan with the latest data.",
            lastUpdated: 'N/A',
            status: 'pending',
            action: 'update_now',
            actionText: 'Update Now',
            actionDisabled: true,
            actionMessage: 'Complete Steps 1-5 first',
            completed: false,
            scriptName: 'update_production_suggestions.py'
          },
          {
            id: 7,
            step: "Update Component Ordering Suggestions",
            description: "Using the latest production plan and bill of materials, update the corresponding component ordering plan",
            lastUpdated: 'N/A',
            status: 'pending',
            action: 'update_now',
            actionText: 'Update Now',
            actionDisabled: true,
            actionMessage: 'Complete Steps 1-6 first',
            completed: false,
            scriptName: 'update_component_ordering_plan.py'
          },
          {
            id: 8,
            step: "Update Financial Forecasting",
            description: "Using the latest sales forecast and global assumptions and production and component ordering plan, project the next 12 months of profit, working capital, and cash flow",
            lastUpdated: financialForecastDate,
            status: 'pending',
            action: 'update_now',
            actionText: 'Update Now',
            actionDisabled: true,
            actionMessage: 'Complete Steps 1-7 first',
            completed: false,
            scriptName: 'update_PL_forecast.py'
          }
        ];

        setSteps(stepsData);
        setError('');
        
        // Update dependent steps after initialization
        setTimeout(() => {
          updateDependentSteps();
        }, 100);
      } catch (error) {
        console.error('Error initializing steps:', error);
        setError('Failed to initialize SOP process');
      } finally {
        setLoading(false);
      }
    };

    initializeSteps();
  }, []);

  // Fetch last updated date for different inventory types
  const fetchLastUpdatedDate = async (type) => {
    try {
      let endpoint = '';
      let query = '';
      
      switch (type) {
        case 'amazon_inventory':
          endpoint = '/api/inventory/last-updated';
          query = '?location=FBA,AWD';
          break;
        case 'austin_inventory':
          endpoint = '/api/inventory/last-updated';
          query = '?location=Austin';
          break;
        case 'component_inventory':
          endpoint = '/api/component-inventory/last-updated';
          break;
        case 'financial_forecast':
          endpoint = '/api/financial-forecast/last-updated';
          break;
        default:
          return 'N/A';
      }

      const response = await axios.get(`${API_BASE_URL}${endpoint}${query}`);
      return response.data.lastUpdated || 'N/A';
    } catch (error) {
      console.error(`Error fetching last updated date for ${type}:`, error);
      return 'N/A';
    }
  };

  // Handle step completion toggle
  const toggleStepCompletion = (stepId) => {
    setSteps(prevSteps => 
      prevSteps.map(step => 
        step.id === stepId 
          ? { ...step, completed: !step.completed, status: !step.completed ? 'completed' : 'pending' }
          : step
      )
    );
    
    // Update dependent steps
    updateDependentSteps();
  };

  // Update dependent steps based on completion status
  const updateDependentSteps = () => {
    setSteps(prevSteps => {
      const updatedSteps = [...prevSteps];
      
      // Check if steps 1-5 are completed for step 6
      const steps1to5Completed = updatedSteps.slice(0, 5).every(step => step.completed);
      updatedSteps[5].actionDisabled = !steps1to5Completed;
      updatedSteps[5].actionMessage = steps1to5Completed ? '' : 'Complete Steps 1-5 first';
      
      // Check if steps 1-6 are completed for step 7
      const steps1to6Completed = steps1to5Completed && updatedSteps[5].completed;
      updatedSteps[6].actionDisabled = !steps1to6Completed;
      updatedSteps[6].actionMessage = steps1to6Completed ? '' : 'Complete Steps 1-6 first';
      
      // Check if steps 1-7 are completed for step 8
      const steps1to7Completed = steps1to6Completed && updatedSteps[6].completed;
      updatedSteps[7].actionDisabled = !steps1to7Completed;
      updatedSteps[7].actionMessage = steps1to7Completed ? '' : 'Complete Steps 1-7 first';
      
      return updatedSteps;
    });
  };

  // Handle action button clicks
  const handleAction = async (step) => {
    console.log('handleAction called with step:', step);
    if (step.actionDisabled) {
      console.log('Step is disabled, returning');
      return;
    }

    try {
      switch (step.action) {
        case 'fetch_now':
          console.log('Calling handleFetchAmazonInventory');
          await handleFetchAmazonInventory();
          break;
        case 'update_now':
          if (step.redirectTo) {
            console.log('Navigating to:', step.redirectTo);
            navigate(step.redirectTo);
          } else if (step.scriptName) {
            console.log('Calling handleRunScript with:', step.scriptName);
            await handleRunScript(step.scriptName, step.id);
          }
          break;
        default:
          console.log('No action matched');
          break;
      }
    } catch (error) {
      console.error('Error handling action:', error);
      setError(`Failed to execute action: ${error.message}`);
    }
  };

  // Handle fetching Amazon inventory
  const handleFetchAmazonInventory = async () => {
    console.log('handleFetchAmazonInventory called');
    setLoading(true);
    setStepMessages(prev => ({ ...prev, 1: { processing: 'Fetching Amazon inventory data...', completion: '' } }));
    try {
      // First fetch the inventory data
      const fetchResponse = await axios.post(`${API_BASE_URL}/api/amazon-inventory/fetch-data`, {}, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!fetchResponse.data.success) {
        throw new Error(fetchResponse.data.error || 'Failed to fetch inventory data');
      }

      // Then save to database
      const saveResponse = await axios.post(`${API_BASE_URL}/api/amazon-inventory/save-data`, {
        inventory_data: fetchResponse.data.inventory_data
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!saveResponse.data.success) {
        throw new Error(saveResponse.data.error || 'Failed to save inventory data');
      }

      // Update step status
      setSteps(prevSteps => 
        prevSteps.map(step => 
          step.id === 1 
            ? { ...step, completed: true, status: 'completed' }
            : step
        )
      );
      
      // Update last updated date
      setLastUpdated(prev => ({ ...prev, amazon_inventory: new Date().toISOString() }));
      
      // Update dependent steps
      updateDependentSteps();
      
      // Show completion message
      setStepMessages(prev => ({ ...prev, 1: { processing: '', completion: 'Amazon inventory data updated successfully!' } }));
      
    } catch (error) {
      console.error('Error fetching Amazon inventory:', error);
      setError(`Failed to fetch Amazon inventory: ${error.message}`);
    } finally {
      setLoading(false);
      // Clear messages after 3 seconds
      setTimeout(() => {
        setStepMessages(prev => {
          const newMessages = { ...prev };
          delete newMessages[1];
          return newMessages;
        });
      }, 3000);
    }
  };

  // Handle running scripts
  const handleRunScript = async (scriptName, stepId) => {
    setLoading(true);
    setStepMessages(prev => ({ ...prev, [stepId]: { processing: 'Running Update...', completion: '' } }));
    try {
      const response = await axios.post(`${API_BASE_URL}/api/sop/run-script`, {
        script_name: scriptName
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        // Update step status
        setSteps(prevSteps => 
          prevSteps.map(step => 
            step.id === stepId 
              ? { ...step, completed: true, status: 'completed' }
              : step
          )
        );
        
        // Update dependent steps
        updateDependentSteps();
        
        // Show completion message
        setStepMessages(prev => ({ ...prev, [stepId]: { processing: '', completion: 'Update completed successfully!' } }));
      } else {
        throw new Error(response.data.error || 'Script execution failed');
      }
    } catch (error) {
      console.error('Error running script:', error);
      setError(`Failed to run script: ${error.message}`);
    } finally {
      setLoading(false);
      // Clear messages after 3 seconds
      setTimeout(() => {
        setStepMessages(prev => {
          const newMessages = { ...prev };
          delete newMessages[stepId];
          return newMessages;
        });
      }, 3000);
    }
  };

  return (
    <div className="overall-sop-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Overall S&OP Process</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Follow the steps in order to complete the Sales & Operations Planning process</li>
              <li>Each step must be completed before proceeding to the next</li>
              <li>Green checkmarks indicate completed steps</li>
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="sop-table-container">
          <table className="sop-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Step</th>
                <th>Description</th>
                <th>Last Updated</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step) => (
                <tr key={step.id}>
                  <td className="step-number-cell">
                    <div className="step-number">{step.id}</div>
                  </td>
                  <td className="step-cell">
                    <div className="step-title">{step.step}</div>
                  </td>
                  <td className="description-cell">{step.description}</td>
                  <td className="last-updated-cell">{step.lastUpdated}</td>
                  <td className="status-cell">
                    <span 
                      className={`status-circle ${step.status}`}
                      onClick={() => toggleStepCompletion(step.id)}
                      title={step.status === 'completed' ? 'Click to uncheck' : 'Click to check'}
                    >
                      {step.status === 'completed' && <span className="checkmark">✓</span>}
                    </span>
                  </td>
                                     <td className="action-cell">
                     <button
                       className={`action-button ${step.actionDisabled ? 'disabled' : ''}`}
                       onClick={() => handleAction(step)}
                       disabled={step.actionDisabled}
                       title={step.actionMessage}
                     >
                       {step.actionText}
                     </button>
                     {step.actionMessage && (
                       <div className="action-message">{step.actionMessage}</div>
                     )}
                                           {stepMessages[step.id]?.processing && (
                        <div className="processing-message">
                          <p>{stepMessages[step.id].processing}</p>
                        </div>
                      )}
                      {stepMessages[step.id]?.completion && (
                        <div className="completion-message">
                          <p>{stepMessages[step.id].completion}</p>
                        </div>
                      )}
                   </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        


      </div>
    </div>
  );
};

export default OverallSOP; 